import { ChevronRight } from 'lucide-react';
import { useId, useState } from 'react';
import type { ReactNode } from 'react';
import { cn } from '../lib/cn.ts';

export interface DisclosureProps {
  /** What the row is ("Session waits for you"). */
  title: ReactNode;
  /** The value at rest, read before anyone opens it ("Warning 15 min, critical 60 min"). */
  summary?: ReactNode;
  /** A small leading icon. */
  icon?: ReactNode;
  /** Trailing controls that act without opening the row (a status, a quiet button). */
  actions?: ReactNode;
  defaultOpen?: boolean;
  /** Controlled: an error inside opens it. */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  children: ReactNode;
  className?: string;
}

/**
 * Progressive disclosure for one setting or one group of them: a row that reads as a value
 * (title, summary) and opens into its controls on click. Use it for a form that would otherwise
 * be a wall of always-open inputs; the summary must say what is set, never how to set it.
 */
export function Disclosure({ title, summary, icon, actions, defaultOpen = false, open: openProp, onOpenChange, children, className }: DisclosureProps) {
  const [openState, setOpenState] = useState(defaultOpen);
  const open = openProp ?? openState;
  const bodyId = useId();
  const toggle = () => {
    if (openProp === undefined) setOpenState(!open);
    onOpenChange?.(!open);
  };
  return (
    <div data-slot="disclosure" data-open={open || undefined} className={cn('flex min-w-0 flex-col', className)}>
      <div className="flex min-h-row min-w-0 items-center gap-2">
        <button
          type="button"
          aria-expanded={open}
          aria-controls={bodyId}
          onClick={toggle}
          className="group flex min-h-row min-w-0 flex-1 items-center gap-3 rounded-control px-2 text-left -outline-offset-2 transition-colors duration-(--duration-fast) hover:bg-hover"
        >
          {icon && <span className="flex shrink-0 text-fg-subtle [&_svg]:size-4">{icon}</span>}
          {/* Title and value on one line; on a narrow screen the value goes under the title instead of being cut. */}
          <span className="flex min-w-0 flex-1 flex-col py-2 sm:flex-row sm:items-center sm:gap-3">
            <span className="min-w-0 truncate text-ui font-medium text-fg sm:shrink-0">{title}</span>
            {summary != null && <span className="min-w-0 truncate text-caption text-fg-subtle num sm:ml-auto sm:text-right sm:text-ui">{summary}</span>}
          </span>
          {/* The same fold mark, at the same end, as a collapsible Panel. */}
          <ChevronRight aria-hidden className={cn('size-4 shrink-0 text-fg-subtle transition-transform duration-(--duration-fast) group-hover:text-fg-muted', open && 'rotate-90')} />
        </button>
        {actions != null && <span className="flex shrink-0 items-center gap-1">{actions}</span>}
      </div>
      {open && (
        <div id={bodyId} className="px-2 pb-4 pt-2">
          {children}
        </div>
      )}
    </div>
  );
}
